import { createContext, useContext, useEffect, useState } from "react";

type Mode = "dark" | "light";
type ColorScheme = "teal" | "blue" | "green" | "orange" | "beige" | "yellow";

interface ThemeContextValue {
  theme: Mode;
  colorScheme: ColorScheme;
  toggleTheme: () => void;
  setColorScheme: (scheme: ColorScheme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  colorScheme: "teal",
  toggleTheme: () => {},
  setColorScheme: () => {},
});

const COLOR_SCHEMES: ColorScheme[] = ["teal", "blue", "green", "orange", "beige", "yellow"];

export const COLOR_SCHEME_META: { id: ColorScheme; label: string; swatch: string }[] = [
  { id: "teal",   label: "Teal",   swatch: "hsl(180 100% 40%)" },
  { id: "blue",   label: "Blue",   swatch: "hsl(217 91% 60%)" },
  { id: "green",  label: "Green",  swatch: "hsl(142 71% 45%)" },
  { id: "orange", label: "Orange", swatch: "hsl(25 95% 53%)" },
  { id: "beige",  label: "Beige",  swatch: "hsl(35 80% 50%)" },
  { id: "yellow", label: "Yellow", swatch: "hsl(48 96% 50%)" },
];

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Mode>(() => {
    try {
      const saved = localStorage.getItem("plate-os-theme");
      return saved === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });

  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    try {
      const saved = localStorage.getItem("plate-os-color-scheme") as ColorScheme;
      return COLOR_SCHEMES.includes(saved) ? saved : "teal";
    } catch {
      return "teal";
    }
  });

  useEffect(() => {
    const root = document.documentElement;

    root.classList.remove("dark", "light");
    root.classList.add(theme);

    COLOR_SCHEMES.forEach(s => root.classList.remove(`theme-${s}`));
    if (colorScheme !== "teal") {
      root.classList.add(`theme-${colorScheme}`);
    }

    try {
      localStorage.setItem("plate-os-theme", theme);
      localStorage.setItem("plate-os-color-scheme", colorScheme);
    } catch {}
  }, [theme, colorScheme]);

  const toggleTheme = () => setTheme(t => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeContext.Provider value={{ theme, colorScheme, toggleTheme, setColorScheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
